import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { NavigationService } from '@omni/services/navigation/navigation.service';

/**
 * DOM scraping tool — lets the model answer "what's on my screen?" by
 * walking the current page and extracting title, visible text, interactive
 * controls, and table contents. All helpers are local because this tool's
 * inputs are the DOM, not any service.
 */
@Injectable({ providedIn: 'root' })
export class ScreenTools {
  private readonly navService = inject(NavigationService);

  @AITool(
    'read_screen',
    'Read and analyze the current screen content to answer questions about what the user is seeing. Extracts visible text, page title, buttons, form fields, and data from the current page.',
    [
      {
        name: 'focus_area',
        type: 'string',
        description:
          'Optional: Specific area to focus on (e.g., "main content", "sidebar", "table data", "form fields")',
        required: false,
      },
    ],
  )
  async readScreen(focus_area?: string): Promise<any> {
    try {
      console.log('[ScreenTools] Reading screen, focus area:', focus_area);

      const appRoot = document.querySelector('ion-app') || document.body;

      const pageTitle = this.extractPageTitle();
      const visibleContent = this.extractVisibleContent(appRoot, focus_area);
      const interactiveElements = this.extractInteractiveElements(appRoot);
      const tableData = this.extractTableData(appRoot);

      const currentPage = this.navService.getCurrentPageName() || 'Unknown';

      const screenData = {
        success: true,
        pageTitle,
        currentPage,
        visibleContent,
        interactiveElements,
        tableData,
        focusArea: focus_area || 'full screen',
        timestamp: new Date().toISOString(),
      };

      console.log('[ScreenTools] Screen data extracted:', screenData);
      return screenData;
    } catch (error) {
      console.error('[ScreenTools] Error reading screen:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to read screen' };
    }
  }

  private extractPageTitle(): string {
    // First non-empty match wins; chain ordered by specificity.
    const titleSelectors = [
      'ion-title',
      'ind-page-title .title',
      '.page-title',
      'h1',
      '.header-title',
    ];

    for (const selector of titleSelectors) {
      const element = document.querySelector(selector);
      if (element?.textContent?.trim()) {
        return element.textContent.trim();
      }
    }

    return document.title || 'Untitled Page';
  }

  private extractVisibleContent(root: Element, focusArea?: string): string {
    let targetElement: Element = root;

    if (focusArea) {
      const focusSelectors: Record<string, string> = {
        'main content': 'ion-content, .main-content, main',
        sidebar: '.sidebar, ion-menu, .side-panel',
        'table data': 'table, ion-grid, .data-table',
        'form fields': 'form, ion-list',
        header: 'ion-header, header',
        footer: 'ion-footer, footer',
      };

      const selector = focusSelectors[focusArea.toLowerCase()] || focusArea;
      const focusElement = document.querySelector(selector);
      if (focusElement) {
        targetElement = focusElement;
      }
    }

    const textContent: string[] = [];
    const walker = document.createTreeWalker(targetElement, NodeFilter.SHOW_TEXT, {
      acceptNode: node => {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;

        const style = window.getComputedStyle(parent);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return NodeFilter.FILTER_REJECT;
        }

        const tagName = parent.tagName.toLowerCase();
        if (['script', 'style', 'noscript'].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }

        const text = node.textContent?.trim();
        if (text && text.length > 0) {
          return NodeFilter.FILTER_ACCEPT;
        }

        return NodeFilter.FILTER_REJECT;
      },
    });

    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.textContent?.trim();
      if (text && text.length > 2) {
        // Filter sub-3-char noise like single punctuation marks.
        textContent.push(text);
      }
    }

    const uniqueContent = [...new Set(textContent)];
    // 100-item cap keeps the payload digestible for the model.
    return uniqueContent.slice(0, 100).join(' | ');
  }

  private extractInteractiveElements(root: Element) {
    const buttons: string[] = [];
    const links: string[] = [];
    const inputs: string[] = [];

    root.querySelectorAll('button, ion-button').forEach(btn => {
      const text = btn.textContent?.trim();
      if (text && !buttons.includes(text)) {
        buttons.push(text);
      }
    });

    root.querySelectorAll('a[href]').forEach(link => {
      const text = link.textContent?.trim();
      if (text && !links.includes(text)) {
        links.push(text);
      }
    });

    root.querySelectorAll('input, ion-input, ion-textarea, textarea, ion-select').forEach(input => {
      const label =
        input.getAttribute('placeholder') ||
        input.getAttribute('aria-label') ||
        input.getAttribute('name') ||
        (input as HTMLInputElement).type;
      if (label && !inputs.includes(label)) {
        inputs.push(label);
      }
    });

    return {
      buttons: buttons.slice(0, 20),
      links: links.slice(0, 20),
      inputFields: inputs.slice(0, 20),
    };
  }

  private extractTableData(root: Element) {
    const tables: Array<{ tableIndex: number; headers: string[]; rowCount: number; sampleRows: string[][] }> = [];

    root.querySelectorAll('table, ion-grid').forEach((table, index) => {
      if (tables.length >= 3) return;

      const headers: string[] = [];
      const rows: string[][] = [];

      table.querySelectorAll('th, ion-col[header]').forEach(th => {
        const text = th.textContent?.trim();
        if (text) headers.push(text);
      });

      table.querySelectorAll('tr, ion-row').forEach((tr, rowIndex) => {
        if (rowIndex === 0 && headers.length > 0) return;
        if (rows.length >= 5) return;

        const cells: string[] = [];
        tr.querySelectorAll('td, ion-col').forEach(td => {
          const text = td.textContent?.trim();
          if (text) cells.push(text);
        });

        if (cells.length > 0) {
          rows.push(cells);
        }
      });

      if (headers.length > 0 || rows.length > 0) {
        tables.push({
          tableIndex: index,
          headers,
          rowCount: rows.length,
          sampleRows: rows.slice(0, 3),
        });
      }
    });

    return tables.length > 0 ? tables : null;
  }
}
